<script setup lang="ts">
import {
  Bold,
  Italic,
  Type,
  ChevronDown,
  AlignLeft,
  TextCursorInput,
  Paintbrush,
  Sparkles,
  Highlighter,
  Droplets,
  CircleDashed,
  PanelTop,
  Blend,
  MoreHorizontal,
} from 'lucide-vue-next';

const rows = [
  { key: 'aiPresets', label: 'AI Rewrite', desc: 'Rewrite selected copy with one click.' },
  { key: 'font', label: 'Font', desc: 'Pick a preset font or upload your own brand font.' },
  { key: 'size', label: 'Size', desc: 'Choose a preset size or type in an exact value.' },
  { key: 'bold', label: 'Bold', desc: 'Make the selected text bold.' },
  { key: 'italic', label: 'Italic', desc: 'Toggle italic styling for the selected text.' },
  {
    key: 'align',
    label: 'Alignment',
    desc: 'Control horizontal alignment, vertical alignment, and writing direction.',
  },
  {
    key: 'lineHeight',
    label: 'Line Height / Tracking',
    desc: 'Adjust line spacing and letter spacing.',
  },
  { key: 'stroke', label: 'Stroke', desc: 'Add a text stroke with preset styles.' },
  {
    key: 'textShadow',
    label: 'Text Shadow',
    desc: 'Add shadow presets or tune blur, distance, and color manually.',
  },
  { key: 'fill', label: 'Color', desc: 'Use solid colors or gradients.' },
  {
    key: 'effect',
    label: 'Text Effects',
    desc: 'Switch between visual text treatments like sticker, neon, or clean type.',
  },
  { key: 'highlight', label: 'Highlight', desc: 'Add a highlighted background behind text.' },
  {
    key: 'styleBrush',
    label: 'Style Brush',
    desc: 'Copy one text style and apply it to another block.',
  },
  { key: 'radius', label: 'Corner Radius', desc: 'Adjust the corners of the text box.' },
  { key: 'opacity', label: 'Opacity', desc: 'Control transparency for the whole element.' },
  {
    key: 'more',
    label: 'More',
    desc: 'Open the Design panel (Color, Decoration, Font).',
  },
];
</script>

<template>
  <div class="doc-table-text-toolbar-attrs my-4 min-w-0 overflow-x-auto">
    <table class="doc-tt w-full border-collapse text-[0.875rem] leading-[1.5]">
      <thead>
        <tr>
          <th class="doc-tt-th doc-tt-col1">Control</th>
          <th class="doc-tt-th doc-tt-col2">Name</th>
          <th class="doc-tt-th doc-tt-col3">What it does</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.key">
          <td class="doc-tt-td doc-tt-col1">
            <div class="doc-tt-s">
              <div
                v-if="row.key === 'font'"
                class="inline-flex items-center gap-1.5 rounded-xl px-2.5 h-8"
              >
                <span class="text-sm text-black/80 dark:text-white/80">Inter</span>
                <ChevronDown class="h-3.5 w-3.5 text-black/50 dark:text-white/50" />
              </div>
              <div
                v-else-if="row.key === 'size'"
                class="inline-flex items-center rounded-xl px-2.5 h-8"
              >
                <span class="text-sm font-mono text-black/80 dark:text-white/80">16</span>
              </div>
              <Sparkles
                v-else-if="row.key === 'aiPresets'"
                class="size-5 text-black/70 dark:text-white/70"
              />
              <Bold
                v-else-if="row.key === 'bold'"
                class="size-5 text-black/70 dark:text-white/70"
              />
              <Italic
                v-else-if="row.key === 'italic'"
                class="size-5 text-black/70 dark:text-white/70"
              />
              <AlignLeft
                v-else-if="row.key === 'align'"
                class="size-5 text-black/70 dark:text-white/70"
              />
              <TextCursorInput
                v-else-if="row.key === 'lineHeight'"
                class="size-5 text-black/70 dark:text-white/70"
              />
              <Type
                v-else-if="row.key === 'stroke'"
                class="size-5 text-black/70 dark:text-white/70"
              />
              <PanelTop
                v-else-if="row.key === 'textShadow'"
                class="size-5 text-black/70 dark:text-white/70"
              />
              <Droplets
                v-else-if="row.key === 'fill'"
                class="size-5 text-black/70 dark:text-white/70"
              />
              <Sparkles
                v-else-if="row.key === 'effect'"
                class="size-5 text-black/70 dark:text-white/70"
              />
              <Highlighter
                v-else-if="row.key === 'highlight'"
                class="size-5 text-black/70 dark:text-white/70"
              />
              <Paintbrush
                v-else-if="row.key === 'styleBrush'"
                class="size-5 text-black/70 dark:text-white/70"
              />
              <CircleDashed
                v-else-if="row.key === 'radius'"
                class="size-5 text-black/70 dark:text-white/70"
              />
              <Blend
                v-else-if="row.key === 'opacity'"
                class="size-5 text-black/70 dark:text-white/70"
              />
              <span
                v-else-if="row.key === 'more'"
                class="inline-flex h-8 items-center justify-center rounded-xl border border-black/[0.12] dark:border-white/[0.14] px-2"
              >
                <MoreHorizontal class="w-4 h-4 text-black/50 dark:text-white/50" />
              </span>
            </div>
          </td>
          <td class="doc-tt-td doc-tt-col2">{{ row.label }}</td>
          <td class="doc-tt-td doc-tt-col3">{{ row.desc }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.doc-tt {
  table-layout: fixed;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  overflow: hidden;
}

.dark .doc-tt {
  border-color: rgba(255, 255, 255, 0.08);
}

.doc-tt-th,
.doc-tt-td {
  padding: 0.55rem 0.7rem;
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  word-break: break-word;
  overflow-wrap: anywhere;
}

.dark .doc-tt-th,
.dark .doc-tt-td {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

.doc-tt-th {
  background: rgba(0, 0, 0, 0.03);
  font-weight: 600;
  font-size: 0.84rem;
  color: rgba(0, 0, 0, 0.55);
  letter-spacing: 0.01em;
}

.dark .doc-tt-th {
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.5);
}

.doc-table-text-toolbar-attrs .doc-tt th:nth-child(1),
.doc-table-text-toolbar-attrs .doc-tt td:nth-child(1),
.doc-tt-col1 {
  width: 15% !important;
}

.doc-table-text-toolbar-attrs .doc-tt th:nth-child(2),
.doc-table-text-toolbar-attrs .doc-tt td:nth-child(2),
.doc-tt-col2 {
  width: 15% !important;
}

.doc-table-text-toolbar-attrs .doc-tt th:nth-child(3),
.doc-table-text-toolbar-attrs .doc-tt td:nth-child(3),
.doc-tt-col3 {
  width: 70% !important;
}

tbody tr:last-child td {
  border-bottom: 0;
}

.doc-tt-s {
  pointer-events: none;
  user-select: none;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
}
</style>
